import { IconCaretRight } from "@components/svg/Icons"
import { useDelayedValue, useReduceMemo } from "@hooks/state-hooks"
import { useTopNav } from "@hooks/useTopNav"
import { Link, useRoute } from "eddev/routing"
import { AnimatePresence, MotionProps, motion } from "framer-motion"
import { useEffect, useRef } from "react"
import { tv } from "tailwind-variants"

const fadeVariants: MotionProps = {
  initial: {
    opacity: 0,
    y: -5,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3,
    },
  },
  exit: {
    opacity: 0,
    y: -5,
    transition: {
      duration: 0.0,
    },
  },
  layout: false,
}

export function TopNav() {
  const route = useRoute()
  const topNav = useDelayedValue(useTopNav(), 100)

  let contents = null

  const animKey = useReduceMemo<string>(
    (prev) => {
      if ((topNav?.mode === "buttons" || topNav?.mode === "sublinks") && topNav.key) {
        return topNav.key
      }
      return prev ?? ""
    },
    [topNav],
  )

  if (topNav?.mode === "buttons") {
    contents = topNav.links.map((item, index) => (
      <motion.li key={item.href} {...fadeVariants} layoutDependency={topNav}>
        <Link preferBack href={item.href} className={navButton()}>
          {item.label}
        </Link>
      </motion.li>
    ))
  } else if (topNav?.mode === "sublinks" && route.pathname.startsWith("/awards")) {
    contents = topNav?.siteLinks?.menuItems?.nodes
      ?.map((item: any, index: number) => [
        item.label === "Awards" && (
          <div key={`floating_key_${index}`} className="absolute md:stack-x-3 z-[1] items-center">
            {[
              <motion.li key={`__${topNav.top.href ?? index}`} {...fadeVariants} layoutDependency={topNav}>
                <Link preferBack href={topNav.top.href} className={navButton()} data-active-parent>
                  {topNav.top.label}
                </Link>
              </motion.li>,
              <motion.li key="arrow" {...fadeVariants} layoutDependency={topNav} className="mx-2">
                <IconCaretRight />
              </motion.li>,
              ...topNav.links.map((item, index) => (
                <motion.li key={"sub_" + (item.href ?? index)} {...fadeVariants} layoutDependency={topNav}>
                  <Link preferBack href={item.href} className={navLink()}>
                    {item.label}
                  </Link>
                </motion.li>
              )),
            ]}
          </div>
        ),
        <motion.li key={`_${item.href ?? index}`} {...fadeVariants} layoutDependency={topNav} className="!opacity-0 !pointer-events-none">
          <Link preferBack href={item.href} className={navButton()}>
            {item.label}
          </Link>
        </motion.li>,
      ])
      .flat()
      .filter(Boolean)
  } else if (topNav?.mode === "sublinks") {
    contents = [
      <motion.li key={`__${topNav.top.href}`} {...fadeVariants} layoutDependency={topNav}>
        <Link preferBack href={topNav.top.href} className={navButton()} data-active-parent>
          {topNav.top.label}
        </Link>
      </motion.li>,
      <motion.li key="arrow" {...fadeVariants} layoutDependency={topNav} className="mx-2">
        <IconCaretRight />
      </motion.li>,
      ...topNav.links.map((item, index) => (
        <motion.li key={"sub_" + item.href} {...fadeVariants} layoutDependency={topNav}>
          <Link preferBack href={item.href} className={navLink()}>
            {item.label}
          </Link>
        </motion.li>
      )),
    ]
  } else if (topNav?.mode === "title") {
    contents = (
      <motion.li key={topNav.href} {...fadeVariants} layoutDependency={topNav}>
        <Link preferBack href={topNav.href} className={navButton()} data-active>
          {topNav.title}
        </Link>
      </motion.li>
    )
  }

  const previousPath = useRef<string | null>(null)
  const shouldSkipAnimation = previousPath.current?.includes("awards") || route.pathname.includes("awards")

  useEffect(() => {
    previousPath.current = route.pathname
  }, [route.pathname])

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.ul
          key={animKey}
          className="hidden md:stack-x-3 relative z-[1] items-center"
          initial={
            shouldSkipAnimation
              ? undefined
              : {
                  opacity: 0,
                  y: 0,
                }
          }
          animate={
            shouldSkipAnimation
              ? undefined
              : {
                  opacity: 1,
                  y: 0,
                }
          }
          exit={
            shouldSkipAnimation
              ? undefined
              : {
                  opacity: 0,
                  y: 0,
                }
          }
          transition={
            shouldSkipAnimation
              ? undefined
              : {
                  stiffness: 400,
                }
          }
        >
          <AnimatePresence initial={false}>{contents}</AnimatePresence>
        </motion.ul>
      </AnimatePresence>
      <AnimatePresence>
        {topNav?.mobileButton && (
          <motion.div className="md:hidden flex items-center justify-center" key={topNav?.mobileButton.href ?? topNav.mobileButton.label}>
            <Link href={topNav.mobileButton.href} className="type-title-xs-medium">
              {topNav.mobileButton.label}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

const navButton = tv({
  base: [
    "type-ui-m-light px-[0.9em] h-button rounded-full inline-flex leading-none items-center justify-center relative",
    "hover:bg-grey-light/50 transition-colors duration-300",
    "[ul:not(:has([data-active]))_&]:link-child-active:bg-grey-light link-active:bg-grey-light data-[active-parent]:!bg-grey-light",
  ],
})

const navLink = tv({
  base: [
    "type-ui-s-roman-caps mx-2 h-button rounded-full inline-flex leading-none items-center justify-center underline-offset-[0.2em] relative",
    "underline hover:decoration-black/20 decoration-black/0 transition-colors duration-300",
    "[ul:not(:has(a[data-active]))_&]:link-child-active:decoration-black/30 link-active:decoration-black/30",
  ],
})
